import { createFileRoute } from '@tanstack/react-router'
import * as React from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Th, Td, fmt, fmtDate } from '@/components/data-table'
import { Trash2, AlertTriangle, Pencil } from 'lucide-react'

export const Route = createFileRoute('/_protected/admin/zonas')({
  component: ZonasPage,
})

type Zona = {
  id: number
  code: string
  municipio: string
  name: string
  type: string
  createdAt: number
  cutAt: number
  nameZona: string
  tieneReportes: boolean
  filasPortalCautivo: number
  filasDiasServicio: number
}

type Impacto = {
  cambia: boolean
  nombreActual?: string
  nombreNuevo?: string
  tieneSerie: boolean
  colision: string | null
}

const aFecha = (ms: number) => new Date(ms).toISOString().slice(0, 10)
const aMs = (s: string) => {
  const [y, m, d] = s.split('-').map(Number)
  return new Date(y, m - 1, d).getTime()
}

function ZonasPage() {
  const [editando, setEditando] = React.useState<Zona | null>(null)
  const [filtro, setFiltro] = React.useState('')
  const [mensaje, setMensaje] = React.useState<string | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  const queryClient = useQueryClient()

  const { data: zonas, isLoading } = useQuery({
    queryKey: ['admin-zonas'],
    queryFn: async () => {
      const res = await fetch('/api/admin/zonas', { credentials: 'include' })
      return res.json() as Promise<Zona[]>
    },
  })

  async function borrar(z: Zona) {
    if (!confirm(`¿Eliminar la zona ${z.code} — ${z.nameZona}?`)) return
    setError(null); setMensaje(null)
    const res = await fetch(`/api/admin/zonas/${z.id}`, { method: 'DELETE', credentials: 'include' })
    const data = await res.json()
    if (!res.ok) { setError(data.error ?? `Error ${res.status}`); return }
    setMensaje(`Zona ${z.code} eliminada.`)
    queryClient.invalidateQueries({ queryKey: ['admin-zonas'] })
  }

  const visibles = zonas?.filter((z) => {
    const q = filtro.trim().toLowerCase()
    return !q || z.code.toLowerCase().includes(q) || z.nameZona.toLowerCase().includes(q)
  })

  const huerfanas = zonas?.filter(
    (z) => !z.tieneReportes && z.filasPortalCautivo === 0 && z.filasDiasServicio === 0,
  ) ?? []

  return (
    <div className="space-y-6">
      {huerfanas.length > 0 && (
        <div className="p-4 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-sm flex gap-2">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            <p className="font-medium">
              {huerfanas.length} zona(s) sin datos en ninguna fuente
            </p>
            <p className="text-amber-900/80">
              {huerfanas.map((z) => `${z.code} (${z.nameZona})`).join(', ')} — no aparecen en los Anexos,
              ni en los días de servicio, ni tienen serie de ping. O falta su información, o no deberían estar en el catálogo.
            </p>
          </div>
        </div>
      )}

      {editando && (
        <EditorZona
          zona={editando}
          onCerrar={() => setEditando(null)}
          onGuardado={(msg) => {
            setEditando(null); setMensaje(msg); setError(null)
            queryClient.invalidateQueries({ queryKey: ['admin-zonas'] })
          }}
          onError={setError}
        />
      )}

      {mensaje && <div className="p-4 rounded-lg bg-green-50 border border-green-200 text-green-800 text-sm">{mensaje}</div>}
      {error && <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm">{error}</div>}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <div>
            <CardTitle>Catálogo de zonas</CardTitle>
            <p className="text-sm text-muted-foreground">{zonas?.length ?? 0} zonas registradas.</p>
          </div>
          <Input
            placeholder="Filtrar por código o nombre…"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            className="max-w-xs"
          />
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="p-6 text-sm text-muted-foreground">Cargando…</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <Th>Código</Th><Th>Municipio</Th><Th>Nombre</Th><Th>Activación</Th>
                    <Th>Ping</Th><Th right>Portal cautivo</Th><Th right>Días servicio</Th><Th></Th>
                  </tr>
                </thead>
                <tbody>
                  {visibles?.map((z) => {
                    const huerfana = !z.tieneReportes && z.filasPortalCautivo === 0 && z.filasDiasServicio === 0
                    return (
                      <tr key={z.id} className={huerfana ? 'bg-amber-50/50' : 'hover:bg-slate-50'}>
                        <Td mono>{z.code}</Td>
                        <Td>{z.municipio}</Td>
                        <Td>{z.name}</Td>
                        <Td>{fmtDate(z.createdAt)}</Td>
                        <Td>{z.tieneReportes ? 'sí' : <span className="text-amber-700">no</span>}</Td>
                        <Td right>{fmt(z.filasPortalCautivo)}</Td>
                        <Td right>{fmt(z.filasDiasServicio)}</Td>
                        <td className="px-2 py-2 text-right border-b whitespace-nowrap">
                          <Button variant="ghost" size="sm" onClick={() => { setEditando(z); setMensaje(null); setError(null) }}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost" size="sm"
                            disabled={!huerfana}
                            title={huerfana ? 'Eliminar zona' : 'Tiene datos asociados; no se puede eliminar'}
                            onClick={() => borrar(z)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 disabled:opacity-30"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function EditorZona({
  zona, onCerrar, onGuardado, onError,
}: {
  zona: Zona
  onCerrar: () => void
  onGuardado: (msg: string) => void
  onError: (msg: string) => void
}) {
  const [form, setForm] = React.useState({
    code: zona.code,
    municipio: zona.municipio,
    name: zona.name,
    type: zona.type,
    createdAt: aFecha(zona.createdAt),
    cutAt: aFecha(zona.cutAt),
  })
  const [impacto, setImpacto] = React.useState<Impacto | null>(null)
  const [guardando, setGuardando] = React.useState(false)

  // El nombre de la zona no es una clave foránea: `reportes.name_zona` guarda el texto
  // "municipio-nombre", así que renombrar sin cascada dejaría la serie de ping huérfana.
  React.useEffect(() => {
    const t = setTimeout(async () => {
      const p = new URLSearchParams({ municipio: form.municipio, name: form.name })
      const res = await fetch(`/api/admin/zonas/${zona.id}/impacto?${p}`, { credentials: 'include' })
      if (res.ok) setImpacto(await res.json())
    }, 350)
    return () => clearTimeout(t)
  }, [form.municipio, form.name, zona.id])

  async function guardar() {
    setGuardando(true)
    try {
      const res = await fetch(`/api/admin/zonas/${zona.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          code: form.code,
          municipio: form.municipio,
          name: form.name,
          type: form.type,
          createdAt: aMs(form.createdAt),
          cutAt: aMs(form.cutAt),
        }),
      })
      const data = await res.json()
      if (!res.ok) { onError(data.error ?? `Error ${res.status}`); return }
      onGuardado(
        `Zona ${form.code.trim()} actualizada` +
        (data.reetiquetadas ? `. Se reetiquetaron ${fmt(data.reetiquetadas)} filas de la serie de ping.` : '.'),
      )
    } catch {
      onError('Error de red al guardar')
    } finally {
      setGuardando(false)
    }
  }

  return (
    <Card>
      <CardHeader><CardTitle>Editar {zona.code}</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Campo l="Código" v={form.code} on={(v) => setForm({ ...form, code: v })} />
          <Campo l="Municipio" v={form.municipio} on={(v) => setForm({ ...form, municipio: v })} />
          <Campo l="Nombre" v={form.name} on={(v) => setForm({ ...form, name: v })} />
          <Campo l="Tipo" v={form.type} on={(v) => setForm({ ...form, type: v })} />
          <Campo l="Activación" v={form.createdAt} on={(v) => setForm({ ...form, createdAt: v })} type="date" />
          <Campo l="Corte" v={form.cutAt} on={(v) => setForm({ ...form, cutAt: v })} type="date" />
        </div>

        {impacto?.colision && (
          <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm">
            Ya existe otra zona ({impacto.colision}) con ese municipio y nombre. Cámbialo antes de guardar.
          </div>
        )}

        {impacto?.cambia && !impacto.colision && (
          <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-sm space-y-1">
            <p className="font-medium flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" /> El renombrado afecta a la serie de ping
            </p>
            <p className="font-mono text-xs">{impacto.nombreActual} → {impacto.nombreNuevo}</p>
            {impacto.tieneSerie ? (
              <p>
                Esta zona tiene mediciones en <code>reportes</code>, que se reetiquetarán al guardar.
                Sin eso quedarían huérfanas y la zona aparecería sin datos en el Dashboard.
                Puede tardar unos segundos.
              </p>
            ) : (
              <p>Esta zona no tiene mediciones de ping, así que sólo cambia el catálogo.</p>
            )}
          </div>
        )}

        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={onCerrar}>Cancelar</Button>
          <Button onClick={guardar} disabled={guardando || !!impacto?.colision}>
            {guardando ? 'Guardando…' : 'Guardar'}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

function Campo({
  l, v, on, type = 'text',
}: { l: string; v: string; on: (v: string) => void; type?: string }) {
  return (
    <div className="space-y-1.5">
      <Label>{l}</Label>
      <Input type={type} value={v} onChange={(e) => on(e.target.value)} />
    </div>
  )
}
